import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';

import messages from './messages';

export interface PageActionMenuProps {
	onCloseDropdown?: () => void;
	onDelete: (pageAri: string, pageName: string) => void;
	onDuplicate: (pageAri: string, pageName: string) => void;
	onEditDetails: (pageAri: string, pageName: string) => void;
	pageAri: string;
	pageName: string;
}

export const PageActionMenu = ({
	pageAri,
	pageName,
	onEditDetails,
	onDuplicate,
	onDelete,
	onCloseDropdown,
}: PageActionMenuProps) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	const handleOpenChange = useCallback(() => {
		setIsOpen(!isOpen);
	}, [isOpen]);

	const handleEditDetails = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			e.stopPropagation();
			setIsOpen(false);
			onCloseDropdown?.();
			onEditDetails(pageAri, pageName);
		},
		[onCloseDropdown, onEditDetails, pageAri, pageName],
	);

	const handleDuplicate = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			e.stopPropagation();
			setIsOpen(false);
			onCloseDropdown?.();
			onDuplicate(pageAri, pageName);
		},
		[onCloseDropdown, onDuplicate, pageAri, pageName],
	);

	const handleDelete = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			e.stopPropagation();
			setIsOpen(false);
			onCloseDropdown?.();
			onDelete(pageAri, pageName);
		},
		[onCloseDropdown, onDelete, pageAri, pageName],
	);

	const handleTriggerClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			handleOpenChange();
		},
		[handleOpenChange],
	);

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...triggerProps }) => (
				<IconButton
					{...triggerProps}
					ref={triggerRef}
					appearance="subtle"
					icon={ShowMoreHorizontalIcon}
					label={formatMessage(messages.moreActions)}
					onClick={handleTriggerClick}
					spacing="compact"
				/>
			)}
			onOpenChange={handleOpenChange}
			isOpen={isOpen}
			testId="page-action-menu"
		>
			<DropdownItemGroup>
				<DropdownItem onClick={handleEditDetails} testId="page-action-edit-details">
					{formatMessage(messages.actionEditPageDetails)}
				</DropdownItem>
				<DropdownItem onClick={handleDuplicate} testId="page-action-duplicate">
					{formatMessage(messages.actionDuplicatePage)}
				</DropdownItem>
				<DropdownItem onClick={handleDelete} testId="page-action-delete">
					{formatMessage(messages.actionDeletePage)}
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
